/**
 * Canonical VFS path utilities.
 *
 * Single source of truth for the path helpers that were previously duplicated
 * across products/vfs, branding/vfs, avatars/vfs, and legacy Tech Studio VFS
 * modules. Domain modules re-export from here; domain-specific parsers
 * (parseProductVfsPath, parseBrandVfsPath, ...) stay in their domains.
 *
 * Pure V8-safe module: no 'use node', no convex/server imports.
 */

export function normalizeAbsolutePath(path: string): string {
  const trimmed = path.trim();
  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const parts: string[] = [];

  for (const segment of withLeadingSlash.split('/')) {
    if (!segment || segment === '.') continue;
    if (segment === '..') {
      parts.pop();
      continue;
    }
    parts.push(segment);
  }

  return `/${parts.join('/')}`;
}

export function basename(path: string): string {
  const parts = normalizeAbsolutePath(path).split('/').filter(Boolean);
  return parts[parts.length - 1] ?? '';
}

export function dirname(path: string): string {
  const parts = normalizeAbsolutePath(path).split('/').filter(Boolean);
  parts.pop();
  return `/${parts.join('/')}`;
}

export function pathIsUnder(path: string, root: string): boolean {
  const normalized = normalizeAbsolutePath(path);
  const normalizedRoot = normalizeAbsolutePath(root);
  // Every absolute path is under the filesystem root; without this the
  // prefix check below would look for '//' and never match.
  if (normalizedRoot === '/') return true;
  return (
    normalized === normalizedRoot || normalized.startsWith(`${normalizedRoot}/`)
  );
}

export function workspaceRootPath(workspaceStorageSlug: string): string {
  return `/${workspaceStorageSlug}`;
}

/** Live content mounts. Storage classification separately retains retired keys. */
export const WORKSPACE_VFS_MOUNT_SEGMENTS = ['users', 'drives'] as const;

/** Historical storage identities stay protected during retention and migration. */
export const WORKSPACE_VFS_STORAGE_SEGMENTS = [
  'avatars',
  'blueprints',
  'brands',
  'collections',
  'documents',
  'drives',
  'generations',
  'jobs',
  // Protected, system-owned Memory Kernel mount. Listed here so every memory
  // payload key is forced into private R2 and can never keep a CDN URL.
  'memory',
  'products',
  'projects',
  'shared',
  'skills',
  'techStudio',
  'tmp',
  'users',
] as const;

export type WorkspaceVfsMountSegment =
  (typeof WORKSPACE_VFS_MOUNT_SEGMENTS)[number];

export const WORKSPACE_VFS_MOUNT_SEGMENT_SET: ReadonlySet<string> = new Set(
  WORKSPACE_VFS_MOUNT_SEGMENTS
);

export const WORKSPACE_VFS_STORAGE_SEGMENT_SET: ReadonlySet<string> = new Set(
  WORKSPACE_VFS_STORAGE_SEGMENTS
);

export function workspaceChildPath(
  workspaceStorageSlug: string,
  child: string
): string {
  return normalizeAbsolutePath(`/${workspaceStorageSlug}/${child}`);
}

export type SlugifyVfsSegmentOptions = {
  /**
   * Strip diacritics via NFKD normalization before slugging ("café" → "cafe").
   * branding/avatars historically strip; products/tech_studio do not
   * ("café" → "caf"). Slugs materialize into stored paths, so each domain
   * must keep its historical behavior — domain shims bind this explicitly.
   */
  stripDiacritics?: boolean;
};

export function slugifyVfsSegment(
  value: string,
  fallback = 'item',
  options?: SlugifyVfsSegmentOptions
): string {
  const lowered = value.trim().toLowerCase();
  const base = options?.stripDiacritics
    ? lowered.normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    : lowered;
  const slug = base
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/--+/g, '-');
  return slug || fallback;
}
